import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Wires up dialog keyboard behavior and returns a ref to attach to the dialog
 * container (give that element tabIndex={-1} so it can receive focus):
 *   - moves focus into the dialog on open (unless something inside autofocused),
 *   - closes on Escape,
 *   - restores focus to the previously-focused element when it unmounts,
 *   - optionally traps Tab within the dialog (trapFocus).
 *
 * Use trapFocus: false for full-screen "panel" views that sit alongside other
 * navigation (e.g. tab content), so keyboard users can still reach that nav.
 */
export default function useDialog({ onClose, trapFocus = true, active = true } = {}) {
  const ref = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active || typeof document === 'undefined') return undefined;
    const node = ref.current;
    const previouslyFocused = document.activeElement;

    if (node && !node.contains(document.activeElement)) {
      const focusables = node.querySelectorAll(FOCUSABLE);
      (focusables[0] || node).focus?.();
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (!trapFocus || e.key !== 'Tab' || !node) return;
      const focusables = Array.from(node.querySelectorAll(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusables.length === 0) { e.preventDefault(); node.focus?.(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [active, trapFocus]);

  return ref;
}
